import { PageResult } from '@/api/types'

export interface PagedResult<T> {
    items: T[]
    /** More pages exist than were fetched */
    truncated: boolean
    /** A page failed; the items are the unbroken run of pages before it */
    failed: boolean
}

/**
 * Page 1 first, for the page count; then pages 2 up to maxPages in parallel. Only the unbroken run of pages from
 * page 1 is kept: with a newest-first (or any) order, what's missing after a failed page is always the end of the
 * order, never a hole in the middle (page 3 can arrive while page 2 failed).
 *
 * A page that resolves to undefined (RestClient.getPaged logs and swallows most errors) or throws (it does when an
 * error body isn't JSON, such as an HTML 502) counts as failed.
 */
export async function fetchPagesInOrder<T>(
    fetchPage: (page: number) => Promise<PageResult<T>|undefined>,
    maxPages: number,
): Promise<PagedResult<T>> {
    const safeFetch = async (page: number): Promise<PageResult<T>|undefined> => {
        try {
            return await fetchPage(page)
        } catch (error) {
            console.log(error)
            return undefined
        }
    }

    const first = await safeFetch(1)
    if (!first) {
        return { items: [], truncated: false, failed: true }
    }

    const lastPage = Math.min(first.totalPages, maxPages)
    const morePages = Array.from({ length: Math.max(lastPage - 1, 0) }, (_, index) => index + 2)
    const rest = await Promise.all(morePages.map(safeFetch))

    const pages = [first]
    for (const result of rest) {
        if (!result) {
            break
        }
        pages.push(result)
    }

    return {
        items: pages.reduce((all: T[], result) => all.concat(result.collection), []),
        truncated: first.totalPages > maxPages,
        failed: pages.length < rest.length + 1,
    }
}
